import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { SchoolDTO } from 'src/app/common/dtos/school.dto';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { dateTimeFromNgbDate } from 'src/app/common/utilities/date-helpers';
import { BooleanNumber } from 'src/app/common/utilities/enums/boolean-number.enum';
import {
  ApiResponseTemplate,
  PaginationDTO,
} from '../../../common/types/responses/responses-template';
import { FileDTO } from './file.dto';

export interface Conversation {
  id: number;
  user_id: number;
  title: string;
  created_at: number;
  updated_at: number;
  glai_stream_id: string;
  messages: GlaiMessageDTO[];
  search_preview?: string;
  user?: UserLiteDTO;
}

export interface FeedConversation {
  id: number;
  user_id: number;
  title: string;
  created_at: number;
  updated_at: number;
  glai_stream_id: string;
  messages: GlaiMessageDTO[];
  search_preview?: string;
  user: User;
}

export interface ConversationResponse {
  item: Conversation;
  _meta: PaginationDTO;
}

export interface ConversationsResponse extends ApiResponseTemplate {
  items: Conversation[];
}
export interface FeedConversationResponse {
  item: FeedConversation;
  _meta: PaginationDTO;
}
export interface FeedConversationsResponse extends ApiResponseTemplate {
  items: FeedConversation[];
}

export interface TokenData {
  content: string;
  message_id: string;
  timestamp: string;
  model_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface ChatMessage {
  id: string;
  content: string;
  rawContent?: string;
  thinking?: string;
  isComplete: boolean;
  timestamp: string;
  role: 'user' | 'assistant';
  files?: FileDTO[];
}

export interface ChatFeedMessage {
  id: string;
  content: string;
  rawContent?: string;
  thinking?: string;
  isComplete: boolean;
  created_at: number;
  role: 'user' | 'assistant';
  files?: FileDTO[];
  user_feedback: AIUserFeedback | null;
  parsed_full: ParsedFeedFullMessage;
}

export interface GeneratedOption {
  title: string;
  description: string;
  selected?: boolean;
}
export interface GenerateOptionsResponse {
  competency: string;
  strand: string;
  level: number;
  gi: string;
  options: GeneratedOption[];
}

export interface GlaiMessageDTO {
  id: number;
  glai_conversation_id: number;
  role: string;
  text: string;
  full_message: string;
  glai_payload: string;
  created_at: number;
  updated_at: number;
  is_deleted: BooleanNumber;
  user_feedback: AIUserFeedback | null;
}

export enum AIUserFeedback {
  THUMBS_UP = 'thumbs_up',
  THUMBS_DOWN = 'thumbs_down',
}

export interface GlaiUserFeedbackPayload {
  user_feedback: AIUserFeedback;
  message_text: string;
  role: 'assistant' | 'user';
}

export interface ProformSmartResponsePayload {
  question_id: number;
  form_id: number;
  answers: Array<{
    proform_form_question_id: number;
    text_answer?: string;
    proform_form_question_answer_option_id?: number;
  }>;
}

export interface ProformSmartResponseResult {
  response: string;
}
export interface SmartAssistantFeedFilterDTO {
  search: string;
  userList: UserLiteDTO[] | null;
  district: DistrictSimpleDto | null;
  schoolList: SchoolDTO[] | null;
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  page: number;
  per_page: number;
}
export interface ParsedFullMessage {
  message?: string;
  files?: FileDTO[];
  modes?: string[];
}

export interface ToolSpec {
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
}

export interface Tool {
  toolSpec: ToolSpec;
}

export interface ToolConfig {
  tools: Tool[];
}

export interface MessageContent {
  text: string;
}

/** One turn in the prior conversation. */
export interface MessageHistoryItem {
  role: string;
  content: MessageContent[];
}
export interface ParsedFeedFullMessage {
  message?: string;
  files?: FileDTO[];
  modes?: string[];
  model_id: string;
  input_tokens: number;
  output_tokens: number;
  system_prompt: string;
  tool_config: ToolConfig;
  message_history: MessageHistoryItem[];
}
export interface ToolInputProperty {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description?: string;
  enum?: string[];
  items?: ToolInputProperty;
  properties?: Record<string, ToolInputProperty>;
  required?: string[];
}
export interface ToolInputSchema {
  json: {
    type: 'object';
    properties: Record<string, ToolInputProperty>;
    required?: string[];
  };
}

export const setDefaultSmartAssistantFeedFilterDateRange = (): {
  toDate: NgbDate;
  fromDate: NgbDate;
} => {
  const currentDate = DateTime.now().set({ hour: 23, minute: 59, second: 59 });
  const toDate = currentDate.plus({ days: 7 });
  const fromDate = currentDate.minus({ days: 180 });
  return {
    toDate: new NgbDate(toDate.year, toDate.month, toDate.day),
    fromDate: new NgbDate(fromDate.year, fromDate.month, fromDate.day),
  };
};

export const emptySmartAssistantFeedFilters =
  (): SmartAssistantFeedFilterDTO => ({
    search: '',
    userList: null,
    district: null,
    schoolList: null,
    fromDate: null,
    toDate: null,
    page: 1,
    per_page: 10,
  });

export interface SmartAssistantFeedPayloadItemsDTO {
  search?: string;
  user_id?: number[];
  district_id?: number;
  school_id?: number[];
  from_date?: number;
  to_date?: number;
  per_page: number;
  sort_order: string;
  page: number;
}

export const smartAssistantFeedFilterDTOToSmartAssistantFeedPayloadItemsDTO = (
  filters: SmartAssistantFeedFilterDTO
): SmartAssistantFeedPayloadItemsDTO => {
  const payload: SmartAssistantFeedPayloadItemsDTO = {
    per_page: filters.per_page,
    sort_order: 'desc',
    page: filters.page ?? 1,
  };

  if (filters.search) {
    payload.search = filters.search;
  }

  if (filters.userList && filters.userList.length > 0) {
    payload.user_id = filters.userList.map((user) => user.id);
  }

  if (filters.schoolList && filters.schoolList.length > 0) {
    payload.school_id = filters.schoolList.map((school) => school.id);
  }

  if (filters.district?.id) {
    payload.district_id = filters.district.id;
  }

  if (filters.fromDate) {
    payload.from_date = dateTimeFromNgbDate(filters.fromDate).toSeconds();
  }

  if (filters.toDate) {
    payload.to_date = dateTimeFromNgbDate(filters.toDate)
      .set({ hour: 23, minute: 59, second: 59 })
      .toSeconds();
  }

  return payload;
};
